import * as Constants from '../constants/action-constants';

import { ColumnFilterState, ColumnSortState } from '@vmware-react/clarity';
import {
  ErrorCause,
  PageResponse,
  PaginationParams,
  Request
} from 'vrlcm-gui-types';

export interface PollRequestsStart {
  type: Constants.POLL_REQUESTS_START;
  payload: {
    paginationParams: PaginationParams;
  };
}

export interface PollRequests {
  type: Constants.POLL_REQUESTS;
}

export interface PollRequestsSuccess {
  type: Constants.POLL_REQUESTS_SUCCESS;
  payload: {
    requests: PageResponse<Request>;
  };
}

export interface PollRequestsFailed {
  type: Constants.POLL_REQUESTS_FAILED;
}

export interface PollRequestsStop {
  type: Constants.POLL_REQUESTS_STOP;
}

export interface PollRequestDetailsStart {
  type: Constants.POLL_REQUEST_DETAILS_START;
  payload: {
    id: string;
    isRequestChart: boolean;
  };
}

export interface PollRequestDetails {
  type: Constants.POLL_REQUEST_DETAILS;
  payload: {
    id: string;
  };
}

export interface PollRequestDetailsSuccess {
  type: Constants.POLL_REQUEST_DETAILS_SUCCESS;
  payload: {
    request: Request;
  };
}

export interface PollRequestDetailsFailed {
  type: Constants.POLL_REQUEST_DETAILS_FAILED;
}

export interface PollRequestDetailsStop {
  type: Constants.POLL_REQUEST_DETAILS_STOP;
}

export interface ClearRequestDetails {
  type: Constants.CLEAR_REQUEST_DETAILS;
}

export interface ManagePageChange {
  type: Constants.MANAGE_PAGE_CHANGE;
  payload: {
    page: number;
  };
}

export interface ManagePageSizeChange {
  type: Constants.MANAGE_PAGE_SIZE_CHANGE;
  payload: {
    pageSize: number;
  };
}

export interface ManageFiltersChange {
  type: Constants.MANAGE_FILTERS_CHANGE;
  payload: {
    filters: ColumnFilterState[];
  };
}

export interface ManageSortChange {
  type: Constants.MANAGE_SORT_CHANGE;
  payload: {
    sort: ColumnSortState;
  };
}

export interface SubmitRetryRequest {
  type: Constants.SUBMIT_RETRY_REQUEST;
  payload: {
    requestId: string;
    causeList: ErrorCause[];
    absoluteRedirection: boolean;
  };
}

export interface SubmitRetryRequestsFailed {
  type: Constants.SUBMIT_RETRY_REQUEST_FAILED;
}

export interface ClearRetryRequestFailedStatus {
  type: Constants.CLEAR_RETRY_REQUEST_FAILED_STATUS;
}

export interface SubmitRetryRequestsSuccess {
  type: Constants.SUBMIT_RETRY_REQUEST_SUCCESS;
}

export interface DeleteRequest {
  type: Constants.DELETE_REQUEST;
  payload: {
    requestId: string;
  };
}

export type RequestsAction =
  | PollRequestsStart
  | PollRequests
  | PollRequestsSuccess
  | PollRequestsFailed
  | PollRequestsStop
  | ManagePageChange
  | ManagePageSizeChange
  | ManageFiltersChange
  | ManageSortChange
  | PollRequestDetailsStart
  | PollRequestDetails
  | PollRequestDetailsSuccess
  | PollRequestDetailsFailed
  | PollRequestDetailsStop
  | ClearRequestDetails
  | SubmitRetryRequest
  | SubmitRetryRequestsFailed
  | ClearRetryRequestFailedStatus
  | SubmitRetryRequestsSuccess
  | DeleteRequest;

// request list

export const pollRequestsStart = (
  paginationParams: PaginationParams
): PollRequestsStart => ({
  type: Constants.POLL_REQUESTS_START,
  payload: {
    paginationParams
  }
});

export const pollRequests = (): PollRequests => ({
  type: Constants.POLL_REQUESTS
});

export const pollRequestsSuccess = (
  requests: PageResponse<Request>
): PollRequestsSuccess => ({
  payload: { requests },
  type: Constants.POLL_REQUESTS_SUCCESS
});

export const pollRequestsFailed = (): PollRequestsFailed => ({
  type: Constants.POLL_REQUESTS_FAILED
});

export const pollRequestsStop = (): PollRequestsStop => ({
  type: Constants.POLL_REQUESTS_STOP
});

// datagrid

export const managePageSizeChange = (
  pageSize: number
): ManagePageSizeChange => ({
  payload: { pageSize },
  type: Constants.MANAGE_PAGE_SIZE_CHANGE
});

export const managePageChange = (page: number): ManagePageChange => ({
  payload: { page },
  type: Constants.MANAGE_PAGE_CHANGE
});

export const manageFiltersChange = (
  filters: ColumnFilterState[]
): ManageFiltersChange => ({
  payload: { filters },
  type: Constants.MANAGE_FILTERS_CHANGE
});

export const manageSortChange = (sort: ColumnSortState): ManageSortChange => ({
  payload: { sort },
  type: Constants.MANAGE_SORT_CHANGE
});

// request details

export const pollRequestDetailsStart = (
  id: string,
  isRequestChart: boolean
): PollRequestDetailsStart => ({
  type: Constants.POLL_REQUEST_DETAILS_START,
  payload: { id, isRequestChart }
});

export const pollRequestDetails = (id: string): PollRequestDetails => ({
  type: Constants.POLL_REQUEST_DETAILS,
  payload: { id }
});

export const pollRequestDetailsSuccess = (
  request: Request
): PollRequestDetailsSuccess => ({
  payload: { request },
  type: Constants.POLL_REQUEST_DETAILS_SUCCESS
});

export const pollRequestDetailsFailed = (): PollRequestDetailsFailed => ({
  type: Constants.POLL_REQUEST_DETAILS_FAILED
});

export const pollRequestDetailsStop = (): PollRequestDetailsStop => ({
  type: Constants.POLL_REQUEST_DETAILS_STOP
});

export const clearRequestDetails = (): ClearRequestDetails => ({
  type: Constants.CLEAR_REQUEST_DETAILS
});

// request retry

export const submitRetryRequest = (
  requestId: string,
  causeList: ErrorCause[],
  absoluteRedirection: boolean
): SubmitRetryRequest => ({
  payload: { requestId, causeList, absoluteRedirection },
  type: Constants.SUBMIT_RETRY_REQUEST
});

export const submitRetryRequestFailed = (): SubmitRetryRequestsFailed => ({
  type: Constants.SUBMIT_RETRY_REQUEST_FAILED
});

export const clearRetryRequestFailedStatus = (): ClearRetryRequestFailedStatus => ({
  type: Constants.CLEAR_RETRY_REQUEST_FAILED_STATUS
});

export const submitRetryRequestSuccess = (): SubmitRetryRequestsSuccess => ({
  type: Constants.SUBMIT_RETRY_REQUEST_SUCCESS
});

export const deleteRequest = (requestId: string): DeleteRequest => ({
  type: Constants.DELETE_REQUEST,
  payload: { requestId }
});
